import { TRootOptions } from '@/components';
import { PageSize, ROUTERS } from '@/constant';
import { GetListSearchTypes } from '@/services';
import { formatSearchParams, insertObjectIfV2 } from '@/utils';
import { isMobile } from '@/utils/mobile.util';
import { useCallback, useMemo } from 'react';
import {
	useLocation,
	useParams,
	useSearchParams as _useSearchParams,
} from 'react-router-dom';

import { includes } from 'ramda';

export const searchParamsKey = 'query_props';

export type SearchParamsType<T = {}> = {
	page?: number;
	pageSize?: PageSize;
	options?: TRootOptions['value'][];
	searchTypes?: GetListSearchTypes['searchTypes'];
} & T;

export const searchParamsTypeKeys: (keyof SearchParamsType)[] = [
	'page',
	'pageSize',
	'options',
	'searchTypes',
];

const IGNORE_SCREEN_SEARCH_PARAMS = [
	ROUTERS.ED1_01,
	ROUTERS.FM1_01,
	ROUTERS.PA2_01,
];

type UseSearchParamsReturnType<T = {}> = [
	SearchParamsType<T> | T,
	(data: SearchParamsType<T>) => void,
	() => void,
];

export const useSearchParams = <T = {},>(): UseSearchParamsReturnType<T> => {
	const { pathname } = useLocation();
	const { id } = useParams<{ id: string }>();

	/**
	 * @description Only search params in list SCREEN-01
	 */
	const searchParamsCheck = useMemo(() => {
		if (!id && !includes(pathname, IGNORE_SCREEN_SEARCH_PARAMS)) {
			return true;
		} else {
			return false;
		}
	}, [id, pathname]);

	const [_searchParams, _setSearchParams] = _useSearchParams();

	const searchParams = useMemo<SearchParamsType<T>>(
		() =>
			formatSearchParams<SearchParamsType<T>>(
				_searchParams,
				searchParamsKey
			) as SearchParamsType<T>,
		[_searchParams]
	);

	const setSearchParams = useCallback(
		(data: SearchParamsType<T>) => {
			if (!searchParamsCheck) {
				return;
			}

			const newSearchParams = {
				...searchParams,
				...data,
			};

			_setSearchParams(
				{ [searchParamsKey]: JSON.stringify(newSearchParams) },
				insertObjectIfV2(isMobile, {
					replace: true,
				})
			);
		},
		[searchParamsCheck, searchParams, _setSearchParams]
	);

	const clear = useCallback(() => {
		_setSearchParams({});
	}, [_setSearchParams]);

	return [searchParams, setSearchParams, clear];
};

export default useSearchParams;
